'use client';

import * as React from 'react';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';
import { useBackgroundTheme } from './use-background-theme';

/**
 * AuroraWaves — Nordlicht / Aurora Borealis Hintergrund
 *
 * Technik: CSS + framer-motion Gradient-Blobs mit blur-Filter
 * CPU-Impact: Very Low (nur CSS-Transitions, GPU-composited, kein JS-Loop)
 *
 * Verwendung:
 *   <AuroraWavesBackground>
 *     <div className="relative z-10">Content</div>
 *   </AuroraWavesBackground>
 */
function AuroraWavesBackground({
  className,
  children,
  speed = 1,
  opacity = 1,
  colors,                  // auto-set from theme if not given
  blurAmount = 80,
  variant = 'auto',        // 'auto' | 'dark' | 'light'
  ...props
}) {
  const isDark = useBackgroundTheme(variant);

  const resolvedColors = colors ?? (isDark
    ? ['#06b6d4', '#8b5cf6', '#10b981', '#3b82f6', '#ec4899']
    : ['#0ea5e9', '#a855f7', '#34d399', '#60a5fa', '#f472b6']);

  const bgClass = isDark ? 'bg-slate-950' : 'bg-white';
  const vignetteClass = isDark ? 'to-slate-950/60' : 'to-white/60';

  const dur = (base) => base / speed;

  const blobs = React.useMemo(() => [
    {
      id: 0,
      color: resolvedColors[0],
      size: 800,
      x: ['15%', '55%', '30%', '70%', '15%'],
      y: ['20%', '60%', '80%', '10%', '20%'],
      duration: dur(22),
      delay: 0,
    },
    {
      id: 1,
      color: resolvedColors[1],
      size: 650,
      x: ['70%', '20%', '80%', '40%', '70%'],
      y: ['10%', '70%', '30%', '80%', '10%'],
      duration: dur(28),
      delay: -6,
    },
    {
      id: 2,
      color: resolvedColors[2 % resolvedColors.length],
      size: 700,
      x: ['40%', '80%', '10%', '60%', '40%'],
      y: ['60%', '20%', '50%', '40%', '60%'],
      duration: dur(25),
      delay: -12,
    },
    {
      id: 3,
      color: resolvedColors[3 % resolvedColors.length],
      size: 500,
      x: ['80%', '40%', '60%', '20%', '80%'],
      y: ['80%', '30%', '10%', '60%', '80%'],
      duration: dur(32),
      delay: -18,
    },
  ], [resolvedColors, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      data-slot="aurora-waves-background"
      className={cn('relative size-full overflow-hidden', bgClass, className)}
      {...props}
    >
      {/* Aurora Blobs */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ opacity }}
      >
        {blobs.map((blob) => (
          <motion.div
            key={blob.id}
            className="absolute rounded-full"
            style={{
              width: blob.size,
              height: blob.size,
              background: blob.color,
              filter: `blur(${blurAmount}px)`,
              mixBlendMode: isDark ? 'screen' : 'normal',
              opacity: isDark ? 1 : 0.22,
              translateX: '-50%',
              translateY: '-50%',
            }}
            animate={{
              left: blob.x,
              top: blob.y,
            }}
            transition={{
              duration: blob.duration,
              delay: blob.delay,
              repeat: Infinity,
              ease: 'easeInOut',
              repeatType: 'loop',
            }}
          />
        ))}
      </div>

      {/* Subtle vignette overlay */}
      <div className={cn('absolute inset-0 bg-radial from-transparent via-transparent pointer-events-none', vignetteClass)} />

      {/* Content Layer */}
      {children && (
        <div className="relative z-10 size-full">{children}</div>
      )}
    </div>
  );
}

export { AuroraWavesBackground };
